//Third Party Dependencies
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
	Card,
	CardHeader,
	Col,
	Collapse,
	Row
} from 'reactstrap';

//MHD General Purpose libraries
import LoadingIndicator from '../../components/loadingIndicator';

//Specific Business and UI components
import * as userActions from '../../actions/userActions';
import ManageUserInfo from './comps/manageInformation';
import ManageSecretQuestions from './comps/manageSecretQuestions';
import ChangePassword from './comps/changePassword';
import { useAppDispatch, useAppSelector } from "../../store";
import './css/userProfileView.css'

function UserProfileView(props: any) {

	const [error, setError] = useState<string>('');
	const [isChangePasswordOpen, setIsChangePasswordOpen] = useState<boolean>(false);
	const [isSecretQuestionsOpen, setIsSecretQuestionsOpen] = useState<boolean>(false);
	const [isUserInfoOpen, setIsUserInfoOpen] = useState<boolean>(true);
	const [isUserInfoLoading, setIsUserInfoLoading] = useState<boolean>(false);
	const [isSecretQuestionsLoading, setIsSecretQuestionsLoading] = useState<boolean>(false);
	const [isChangePasswordLoading, setIsChangePasswordLoading] = useState<boolean>(false);
	const [isPageLoading, setIsPageLoading] = useState<boolean>(false);

	const details: any = useAppSelector((state: any) => state.user.details);
	const changePasswordResponse: any = useAppSelector((state: any) => state.user.changePassword);
	const userInfoConfig: any = useAppSelector((state: any) => state.app.config.userInfo);
	const passwordConfig: any = useAppSelector((state: any) => state.config.passwordConfig);

	const dispatch = useAppDispatch();

	const saveUserProfile = (userInfo: any) => {
		updateUserProfile(userInfo, setIsUserInfoLoading)
	}

	const saveSecretQuestions = (userInfo: any) => {
		updateUserProfile(userInfo, setIsSecretQuestionsLoading)
	}

	const updateUserProfile = (userInfo: any, isLoading: Function) => {
		isLoading(true);

		dispatch(userActions.updateUserProfile(userInfo)).then((response: any) => {
			if (response.error) {
				setError('An error occurred while trying to update the user profile.');
			}
			isLoading(false);
		})
			.catch((err: string) => {
				setError(`Error: ${err}`);
				isLoading(false);
			});
	}

	const updatePassword = (currentPassword: string, newPassword: string, confirmPassword: string) => {
		setIsChangePasswordLoading(true);

		var passwordInfo = {
			currentPassword: currentPassword,
			newPassword: newPassword,
			confirmPassword: confirmPassword,
		}

		dispatch(userActions.updateUserPassword(passwordInfo))
			.then((response: any) => {
				if (response.error) {
					setError('There was an error updating your password.');
				}
				setIsChangePasswordLoading(false);
			})
			.catch((err: any) => {
				setError('There was an error updating your password.');
				setIsChangePasswordLoading(false);
			});
	}

	const loadUserProfile = () => {
		setIsPageLoading(true);
		dispatch(userActions.getUserProfile())
			.then((response: any) => {
				if (response.error) {
					setError('An error occurred while trying to load the user profile.');
				}
				setIsPageLoading(false);
			})
			.catch((err: any) => {
				setError("Error")
				setIsPageLoading(false);
			});
	}

	const onUserInfoClick = (event: any) => {
		event.preventDefault();
		setIsUserInfoOpen(!isUserInfoOpen);
		setIsSecretQuestionsOpen(false);
		setIsChangePasswordOpen(false);
	}

	const onSecretQuestionsClick = (event: any) => {
		event.preventDefault();
		setIsUserInfoOpen(false);
		setIsSecretQuestionsOpen(!isSecretQuestionsOpen);
		setIsChangePasswordOpen(false);
	}

	const onChangePasswordClick = (event: any) => {
		event.preventDefault();
		setIsUserInfoOpen(false);
		setIsSecretQuestionsOpen(false);
		setIsChangePasswordOpen(!isChangePasswordOpen);
	}

	useEffect(() => {
		loadUserProfile();
	}, []);

	let chevronRight = <FontAwesomeIcon style={{ "marginLeft": "10px" }} icon={faChevronRight} size="sm" />;
	let chevronDown = <FontAwesomeIcon style={{ "marginLeft": "10px" }} icon={faChevronDown} size="sm" />;;

	let chevronUserInfo = chevronRight;
	if (isUserInfoOpen) {
		chevronUserInfo = chevronDown;
	} else {
		chevronUserInfo = chevronRight;
	}

	let chevronSecretQuestions = chevronRight;
	if (isSecretQuestionsOpen) {
		chevronSecretQuestions = chevronDown;
	} else {
		chevronSecretQuestions = chevronRight;
	}

	let chevronChangePassword = chevronRight;
	if (isChangePasswordOpen) {
		chevronChangePassword = chevronDown;
	} else {
		chevronChangePassword = chevronRight;
	}

	return (
		<>
			{isPageLoading &&
				<LoadingIndicator />
			}
			{isPageLoading ? '' :
				<div>
					<h4>User Profile</h4>
					<div className="content">
						<Row className="g-0 sectionRow">
							<Col lg="12">
								<Card>
									<CardHeader className="cardHeader" onClick={onUserInfoClick}><h5>User Information {chevronUserInfo}</h5></CardHeader>
									<Collapse isOpen={isUserInfoOpen}>
										{isUserInfoLoading &&
											<LoadingIndicator />
										}
										{!isUserInfoLoading &&
											<ManageUserInfo details={details} onSubmit={saveUserProfile} />
										}
									</Collapse>
								</Card>
							</Col>
						</Row>
						<Row className="g-0 sectionRow">
							<Col lg="12">
								<Card>
									<CardHeader className="cardHeader" onClick={onSecretQuestionsClick}><h5>Manage Security Questions {chevronSecretQuestions}</h5></CardHeader>
									<Collapse isOpen={isSecretQuestionsOpen}>
										{isSecretQuestionsLoading &&
											<LoadingIndicator />
										}
										{!isSecretQuestionsLoading &&
											<ManageSecretQuestions
												isFirstLogin={false}
												editMode={false}
												details={details}
												onSubmit={saveSecretQuestions}
												questionLength={userInfoConfig.secretQuestion.questionLength}
												answerLength={userInfoConfig.secretQuestion.answerLength} />
										}
									</Collapse>
								</Card>
							</Col>
						</Row>
						<Row className="g-0 sectionRow">
							<Col lg="12">
								<Card>
									<CardHeader className="cardHeader" onClick={onChangePasswordClick}><h5>Change Password {chevronChangePassword}</h5></CardHeader>
									<Collapse isOpen={isChangePasswordOpen}>
										{isChangePasswordLoading &&
											<LoadingIndicator />
										}
										{!isChangePasswordLoading &&
											<ChangePassword
												onSubmit={updatePassword}
												response={changePasswordResponse}
												passwordConfig={passwordConfig}
											/>
										}
									</Collapse>
								</Card>
							</Col>
						</Row>
					</div>
				</div>
			}
		</>
	)
}

export default UserProfileView;
